import React, { useState, useEffect } from 'react';
import { router, usePage } from '@inertiajs/react';
import { calculateProfileCompleteness } from '@/Utils/profileUtils';

const WriterPortfolioSetup = () => {
  const { user, scripts, urls } = usePage().props;
  
  const [formData, setFormData] = useState({
    display_name: user.name || '',
    nickname: '',
    specialty_tagline: '',
    bio: '',
    open_to_writers_group: false,
    open_to_writing_partner: false,
  });
  
  const [selectedScripts, setSelectedScripts] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [publishConsent, setPublishConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allScripts = scripts || [];
  
  // Show available scripts (not selected)
  const availableScripts = allScripts.filter(script => 
    !selectedScripts.find(selected => selected.id === script.id)
  );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addScript = (script) => {
    setSelectedScripts(prev => [...prev, script]);
  };

  const removeScript = (scriptId) => {
    setSelectedScripts(prev => prev.filter(script => script.id !== scriptId));
  };

  // Drag and drop for reordering selected scripts
  const moveScript = (dragIndex, hoverIndex) => {
    const draggedScript = selectedScripts[dragIndex];
    const newOrder = [...selectedScripts];
    newOrder.splice(dragIndex, 1);
    newOrder.splice(hoverIndex, 0, draggedScript);
    setSelectedScripts(newOrder);
  };

  const isFormValid = () => {
    return formData.display_name.trim() && formData.nickname.trim() && formData.bio.trim();
  };

  const calculateCompleteness = () => {
    // In the setup flow, 'selectedScripts' are the ones that will be public
    return calculateProfileCompleteness(formData, selectedScripts);
  };

  const isUsernameAvailable = (username) => {
    const unavailable = ['admin', 'test', 'user', 'scriptreader'];
    return username && !unavailable.includes(username.toLowerCase());
  };

// In WriterPortfolioSetup.js, fix the createPortfolio function:

const createPortfolio = async () => {
  setIsSubmitting(true);
  setIsPublic(true);
  
  try {
    console.log('Creating portfolio with data:', {
      ...formData,
      is_public: true,
      selected_scripts: selectedScripts.map(script => script.id), // Current: sending IDs
    });

    const response = await fetch(urls.store, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
      },
      body: JSON.stringify({
        ...formData,
        is_public: true,
        selected_scripts: selectedScripts.map(script => script.id), // script.id is actually the hash
      }),
    });

    // Enhanced error logging
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Server response:', response.status, response.statusText);
      console.error('Response body:', errorText);
      
      try {
        const errorData = JSON.parse(errorText);
        console.error('Parsed error data:', errorData);
        
        if (errorData.errors) {
          const errorMessages = Object.entries(errorData.errors)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          throw new Error(`Validation failed:\n${errorMessages}`);
        }
        
        throw new Error(errorData.message || 'Portfolio creation failed');
      } catch (parseError) {
        throw new Error(`Server error (${response.status}): ${errorText.slice(0, 200)}...`);
      }
    }

    const result = await response.json();
    console.log('Success response:', result);

    if (result.success) {
      setShowSuccessModal(true);
    } else {
      throw new Error(result.message || 'Portfolio creation failed');
    }
  } catch (error) {
    console.error('Portfolio creation error:', error);
    alert('Failed to create portfolio: ' + error.message);
  } finally {
    setIsSubmitting(false);
  }
};

  const handleBackToLanding = () => {
    router.visit('/writer-profile/create');
  };

  const goToEdit = () => {
    router.visit('/writer-profile/edit');
  };

  const goToMyScripts = () => {
    router.visit('/my-scripts');
  };

  return (
    <div className="min-vh-100 bg-light">
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="card shadow-lg border-0">
              {/* Header */}
              <div className="card-header text-white py-5 border-0" 
                   style={{background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)'}}>
                <h2 className="display-6 fw-semibold mb-2">Set Up Your Portfolio</h2>
                <p className="lead opacity-90 mb-0">Just the essentials - you can always add more later</p>
              </div>

              <div className="card-body p-5">
                {/* Basic Information Section */}
                <div className="mb-5">
                  <h3 className="h4 fw-semibold mb-4 text-dark d-flex align-items-center">
                    <span className="me-3">👤</span>
                    Basic Information
                  </h3>

                  <div className="row g-4">
                    <div className="col-12">
                      <label className="form-label fw-semibold text-dark">
                        Your name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.display_name}
                        onChange={(e) => handleInputChange('display_name', e.target.value)}
                        placeholder="e.g., Sarah Johnson"
                        className={`form-control form-control-lg ${
                          formData.display_name.trim() 
                            ? 'is-valid' 
                            : 'border-secondary'
                        }`}
                      />
                      <div className="form-text text-muted">
                        This is how other writers and industry professionals will see you
                      </div>
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold text-dark">
                        Portfolio URL <span className="text-danger">*</span>
                      </label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text bg-light text-muted border-end-0">
                          scriptreader.ai/portfolios/
                        </span>
                        <input
                          type="text"
                          value={formData.nickname}
                          onChange={(e) => handleInputChange('nickname', e.target.value)}
                          placeholder="your-name"
                          className={`form-control border-start-0 ${
                            isUsernameAvailable(formData.nickname) 
                              ? 'is-valid' 
                              : 'border-secondary'
                          }`}
                        />
                        <span className="input-group-text bg-light border-start-0">
                          <span className={`fs-5 ${
                            isUsernameAvailable(formData.nickname) ? 'text-success' : 'text-danger'
                          }`}>
                            {isUsernameAvailable(formData.nickname) ? '✓' : '✗'}
                          </span>
                        </span>
                      </div>
                      <div className="form-text text-muted">Choose a memorable URL to share with others</div>
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold text-dark">
                        What you write
                      </label>
                      <input
                        type="text"
                        value={formData.specialty_tagline}
                        onChange={(e) => handleInputChange('specialty_tagline', e.target.value)}
                        placeholder="e.g., Character-driven sci-fi thrillers"
                        className={`form-control form-control-lg ${
                          formData.specialty_tagline.trim() ? 'is-valid' : 'border-secondary'
                        }`}
                        maxLength="100"
                      />
                      <div className="form-text text-muted d-flex justify-content-between">
                        <span>One line describing your specialty or favorite genres</span>
                        <span className="text-muted">{100 - (formData.specialty_tagline?.length || 0)}</span>
                      </div>
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold text-dark">
                        Brief bio <span className="text-danger">*</span>
                      </label>
                      <textarea
                        rows="4"
                        value={formData.bio}
                        onChange={(e) => handleInputChange('bio', e.target.value)}
                        placeholder="Tell visitors about your writing background, what drives your storytelling, and what makes your scripts unique..."
                        className={`form-control ${
                          formData.bio.trim() ? 'is-valid' : 'border-secondary'
                        }`}
                        style={{resize: 'vertical'}}
                      />
                      <div className="form-text text-muted">
                        2-3 sentences about yourself as a writer (required)
                      </div>
                    </div>
                  </div>
                </div>

                {/* Scripts Section */}
                {allScripts.length > 0 && (
                  <div className="mb-5">
                    <h3 className="h4 fw-semibold mb-4 text-dark d-flex align-items-center">
                      <span className="me-3">📄</span>
                      Your Scripts
                      <span className="small text-muted ms-3">(optional - you can add these later)</span>
                    </h3>

                    <div className="alert alert-info border-0 mb-4" style={{backgroundColor: '#EBF8FF'}}>
                      <p className="text-info mb-2">
                        <strong>💡 Pro tip:</strong> Start with your 2-3 best scripts to make a strong first impression.
                      </p>
                      <p className="text-info mb-0 small">
                        You can always add more scripts later from your profile settings.
                      </p>
                    </div>

                    {/* Script Selection Interface */}
                    <div className="row g-4">
                      {/* Available Scripts */}
                      <div className="col-md-7">
                        <h5 className="fw-semibold mb-3">Available Scripts</h5>
                        <div className="border rounded bg-light p-3" style={{maxHeight: '400px', overflowY: 'auto'}}>
                          {availableScripts.length > 0 ? (
                            <div className="list-group list-group-flush">
                              {availableScripts.map(script => (
                                <div key={script.id} className="list-group-item bg-transparent border-0 px-0 py-2">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1 me-3">
                                      <div className="d-flex align-items-center gap-2 mb-1">
                                        <h6 className="mb-0 fw-semibold">{script.title}</h6>
                                        <span className="badge bg-success small">{script.score}</span>
                                      </div>
                                      <div className="small text-muted">
                                        {script.logline ? script.logline.substring(0, 80) + '...' : script.date}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => addScript(script)}
                                      className="btn btn-sm btn-primary"
                                      title="Add to portfolio"
                                    >
                                      <i className="fa fa-plus"></i>
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-muted">
                              <i className="fa fa-check-circle fa-2x mb-2 text-success"></i>
                              <p className="mb-0">All scripts selected!</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Selected Scripts */}
                      <div className="col-md-5">
                        <h5 className="fw-semibold mb-3">
                          Portfolio Scripts ({selectedScripts.length})
                        </h5>
                        <div className="border rounded p-3 bg-white" style={{minHeight: '200px'}}>
                          {selectedScripts.length > 0 ? (
                            <div>
                              <div className="small text-muted mb-3">
                                <i className="fa fa-grip-vertical me-1"></i>
                                Drag to reorder
                              </div>
                              <div className="list-group">
                                {selectedScripts.map((script, index) => (
                                  <div 
                                    key={script.id}
                                    className="list-group-item d-flex justify-content-between align-items-center py-2 px-3"
                                    draggable
                                    onDragStart={(e) => {
                                      e.dataTransfer.setData('text/plain', index);
                                    }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
                                      if (dragIndex !== index) {
                                        moveScript(dragIndex, index);
                                      }
                                    }}
                                    style={{cursor: 'move'}}
                                  >
                                    <div className="d-flex align-items-center flex-grow-1">
                                      <i className="fa fa-grip-vertical text-muted me-2"></i>
                                      <div>
                                        <div className="fw-semibold small">{script.title}</div>
                                        <div className="text-muted" style={{fontSize: '0.75rem'}}>
                                          {script.score} • {script.date}
                                        </div>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => removeScript(script.id)}
                                      className="btn btn-sm btn-outline-danger"
                                      title="Remove from portfolio"
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-5 text-muted">
                              <i className="fa fa-film fa-2x mb-3 opacity-50"></i>
                              <p className="mb-0 small">
                                Select scripts from the left<br/>
                                to showcase in your portfolio
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Scripts Status */}
                    <div className="alert alert-secondary border-0 mt-4">
                      <div className="row text-center">
                        <div className="col-4">
                          <div className="fw-semibold text-dark">{selectedScripts.length}</div>
                          <div className="small text-muted">Scripts selected</div>
                        </div>
                        <div className="col-4">
                          <div className="fw-semibold text-dark">{availableScripts.length}</div>
                          <div className="small text-muted">Available to add</div>
                        </div>
                        <div className="col-4">
                          <div className="fw-semibold text-dark">{calculateCompleteness()}%</div>
                          <div className="small text-muted">Profile complete</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Collaboration Preferences Section */}
                <div className="mb-5">
                  <h3 className="h4 fw-semibold mb-4 text-dark d-flex align-items-center">
                    <span className="me-3">🤝</span>
                    Community & Collaboration
                    <span className="small text-muted ms-3">(optional)</span>
                  </h3>

                  <div className="alert alert-info border-0 mb-4" style={{backgroundColor: '#EBF8FF'}}>
                    <p className="text-info mb-0 small">
                      <i className="fa fa-info-circle me-1"></i>
                      Help us understand what writers want! These features aren't implemented yet, 
                      but your input helps us prioritize development.
                    </p>
                  </div>

                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="card h-100 border-secondary">
                        <div className="card-body">
                          <div className="form-check form-switch">
                            <input
                              type="checkbox"
                              checked={formData.open_to_writers_group}
                              onChange={(e) => handleInputChange('open_to_writers_group', e.target.checked)}
                              className="form-check-input"
                              id="writersGroup"
                            />
                            <label className="form-check-label" htmlFor="writersGroup">
                              <div className="fw-semibold text-dark">Interested in writer's groups</div>
                              <div className="small text-muted mt-1">
                                Local meetups, online communities, critique groups
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="card h-100 border-secondary">
                        <div className="card-body">
                          <div className="form-check form-switch">
                            <input
                              type="checkbox"
                              checked={formData.open_to_writing_partner}
                              onChange={(e) => handleInputChange('open_to_writing_partner', e.target.checked)}
                              className="form-check-input"
                              id="writingPartner"
                            />
                            <label className="form-check-label" htmlFor="writingPartner">
                              <div className="fw-semibold text-dark">Interested in writing partners</div>
                              <div className="small text-muted mt-1">
                                One-on-one collaboration opportunities
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Acknowledgment Section */}
              <div className="px-5 py-4 border-top bg-light">
                <div className="alert alert-warning border-0" style={{backgroundColor: '#FFFBEB'}}>
                  <h5 className="fw-semibold text-warning-emphasis mb-3">
                    <i className="fa fa-exclamation-triangle me-2"></i>
                    Before we create your portfolio
                  </h5>
                  
                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      checked={publishConsent}
                      onChange={(e) => setPublishConsent(e.target.checked)}
                      className="form-check-input"
                      id="publishConsent"
                      required
                    />
                    <label className="form-check-label text-warning-emphasis" htmlFor="publishConsent">
                      <div className="fw-semibold">
                        I understand my portfolio will be publicly accessible at my chosen URL
                      </div>
                      <div className="small mt-1">
                        Your portfolio will be live at <code>scriptreader.ai/portfolios/{formData.nickname || 'your-username'}</code>.
                        You can make it private anytime from your profile settings.
                      </div>
                    </label>
                  </div>

                  <div className="row g-3 small text-warning-emphasis">
                    <div className="col-md-6">
                      <div className="d-flex align-items-start">
                        <i className="fa fa-eye me-2 mt-1"></i>
                        <div>
                          <div className="fw-semibold">Who can see it:</div>
                          <div>Anyone with your portfolio link</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-start">
                        <i className="fa fa-lock me-2 mt-1"></i>
                        <div>
                          <div className="fw-semibold">Privacy control:</div>
                          <div>You can make it private anytime</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="card-footer bg-light border-top d-flex justify-content-between align-items-center py-4">
                <button 
                  onClick={handleBackToLanding}
                  className="btn btn-secondary fw-semibold"
                >
                  ← Back
                </button>

                <div className="d-flex align-items-center gap-3 text-muted small">
                  <span>Almost done</span>
                  <div className="progress" style={{width: '120px', height: '8px'}}>
                    <div className="progress-bar bg-success" style={{width: '80%'}}></div>
                  </div>
                </div>

                <button
                  onClick={() => createPortfolio()}
                  disabled={!isFormValid() || !publishConsent || isSubmitting}
                  className={`btn fw-semibold ${
                    isFormValid() && publishConsent && !isSubmitting
                      ? 'btn-success'
                      : 'btn-secondary'
                  }`}
                  style={{opacity: isFormValid() && publishConsent && !isSubmitting ? 1 : 0.6}}
                >
                  {isSubmitting ? 'Creating...' : 'Create Portfolio →'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🎉 Portfolio Created Successfully!</h5>
                <button
                  onClick={() => setShowSuccessModal(false)}
                  type="button"
                  className="btn-close"
                ></button>
              </div>
              <div className="modal-body text-center py-5">
                <div style={{fontSize: '4rem'}} className="mb-4">🎬</div>
                <h2 className="h3 fw-semibold mb-3">Your portfolio is now {isPublic ? 'live' : 'created'}!</h2>
                <p className="text-muted mb-5 lead">
                  {isPublic 
                    ? 'Share your professional writer portfolio with this link:' 
                    : 'Your private portfolio is ready. You can make it public anytime.'}
                </p>

                <div className="alert alert-secondary mb-5 font-monospace">
                  scriptreader.ai/portfolios/{formData.nickname}
                </div>

                <div className="d-grid gap-3">
                  <button 
                    onClick={goToEdit}
                    className="btn btn-primary btn-lg"
                  >
                    ⚡ Continue Enhancing Profile
                  </button>
                  <button 
                    onClick={goToMyScripts}
                    className="btn btn-outline-secondary"
                  >
                    I'm done for now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WriterPortfolioSetup;
